/**
 * THE HEADER'S LAST CRUMB: THE TAB THE PAGE IS ON (prototype v2, 2026-11-02).
 *
 * The header says WHERE you are as a plain crumb, "Jazz / JAZZMSC5 / Alarms / Analytics". The first
 * three come from the scope and from `nav.ts`; the tab is the page's own state, and the header is not
 * the page's parent, so it cannot be passed down. A page's tab row publishes its active label here
 * (`usePageCrumbTab`, which `StageTabs` and the page tab rows call) and the header reads it
 * (`usePageCrumb`).
 *
 * ONE VALUE, LAST WRITER WINS, CLEARED ON UNMOUNT. Two tab rows are never on one screen at the page
 * level; a card's own segment is a view of that card, not where you are, so it never publishes. The
 * clear is guarded by identity, so a page that unmounts after the next one mounted cannot wipe the
 * newcomer's label.
 *
 * Plain module state rather than Zustand or a context: one string, read by one component, and a
 * context would have to wrap the Shell from `App.tsx`, which is the page's grandparent, not its
 * child.
 */
import { useEffect, useSyncExternalStore } from 'react';

type Listener = () => void;

interface Entry {
  readonly label: string;
}

let current: Entry | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of listeners) l();
}

/** Publish a tab label; returns the function that withdraws exactly this publication. */
export function setPageCrumbTab(label: string | null): () => void {
  const entry = label === null || label.trim() === '' ? null : { label: label.trim() };
  if (current?.label !== entry?.label) {
    current = entry;
    emit();
  } else {
    current = entry ?? current;
  }
  const mine = current;
  return () => {
    if (current !== null && current === mine) {
      current = null;
      emit();
    }
  };
}

export function getPageCrumbTab(): string | null {
  return current?.label ?? null;
}

function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** The header's reader. */
export function usePageCrumb(): string | null {
  return useSyncExternalStore(subscribe, getPageCrumbTab, getPageCrumbTab);
}

/** A page's tab row names its active tab for the header's crumb while it is mounted. */
export function usePageCrumbTab(label: string | null | undefined): void {
  useEffect(() => setPageCrumbTab(label ?? null), [label]);
}
